const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

function getToken(): string {
  return localStorage.getItem('token') || '';
}

export interface TriggerResult {
  taskId: string;
}

export type ExternalAnalysisStatusValue =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

/**
 * Status payload from drama backend's GET status endpoint.
 *
 * Stage arrays mirror partner contract API-drama.md §3.2 (`stages_running`,
 * `stages_completed`, `stages_total`). UI computes progress as
 * `stagesCompleted.length / stagesTotal.length` so it can render n/total
 * granularity instead of a single percent figure.
 *
 * `error` / `failedStep` are the partner-shape failure fields surfaced on
 * terminal failure states.
 */
export interface ExternalAnalysisStatusPayload {
  taskId: string;
  status: ExternalAnalysisStatusValue;
  stagesRunning: string[];
  stagesCompleted: string[];
  stagesTotal: string[];
  // Fine-grained sub-task counter from the partner (advances *within* a stage,
  // so it moves even while stagesCompleted is still empty). `progressTotal === 0`
  // means the partner hasn't published numeric progress yet — the UI then falls
  // back to stage cardinality. See computeAnalysisProgress.
  progressCompleted: number;
  progressTotal: number;
  error?: string;
  failedStep?: string;
}

export class ExternalAnalysisError extends Error {
  code?: string;
  httpStatus?: number;
  constructor(message: string, code?: string, httpStatus?: number) {
    super(message);
    this.name = 'ExternalAnalysisError';
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

async function callTrigger(projectId: string, path: string): Promise<TriggerResult> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new ExternalAnalysisError('未登录', undefined, 401);
  }
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code: string | undefined = typeof payload?.code === 'string' ? payload.code : undefined;
    const msg: string =
      (typeof payload?.error === 'string' && payload.error) ||
      (typeof payload?.message === 'string' && payload.message) ||
      'Request failed';
    throw new ExternalAnalysisError(msg, code, res.status);
  }
  const taskId: string = typeof payload?.taskId === 'string' ? payload.taskId : '';
  if (!taskId) {
    throw new ExternalAnalysisError('服务端未返回任务 ID', undefined, res.status);
  }
  return { taskId };
}

export const triggerExternalAnalysis = (projectId: string): Promise<TriggerResult> =>
  callTrigger(projectId, '/external-analysis/trigger');

export const retryExternalAnalysis = (projectId: string): Promise<TriggerResult> =>
  callTrigger(projectId, '/external-analysis/retry');

const KNOWN_STATUSES: ReadonlySet<ExternalAnalysisStatusValue> = new Set<ExternalAnalysisStatusValue>([
  'pending',
  'running',
  'succeeded',
  'failed',
  'cancelled',
]);

export async function getExternalAnalysisStatus(
  projectId: string,
  taskId: string,
  signal?: AbortSignal,
): Promise<ExternalAnalysisStatusPayload> {
  const token = getToken();
  const res = await fetch(
    `${API_BASE}/projects/${projectId}/external-analysis/${encodeURIComponent(taskId)}/status`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    },
  );
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new ExternalAnalysisError('未登录', undefined, 401);
  }
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code: string | undefined = typeof payload?.code === 'string' ? payload.code : undefined;
    const msg: string =
      (typeof payload?.error === 'string' && payload.error) ||
      (typeof payload?.message === 'string' && payload.message) ||
      'Request failed';
    throw new ExternalAnalysisError(msg, code, res.status);
  }
  // Validate against the known status union; fall back to 'pending' if the
  // backend ever returns an unrecognised value.
  const rawStatus = payload?.status as ExternalAnalysisStatusValue | undefined;
  const status: ExternalAnalysisStatusValue =
    rawStatus !== undefined && KNOWN_STATUSES.has(rawStatus) ? rawStatus : 'pending';
  return {
    taskId: typeof payload?.taskId === 'string' ? payload.taskId : '',
    status,
    stagesRunning: toStringArray(payload?.stagesRunning),
    stagesCompleted: toStringArray(payload?.stagesCompleted),
    stagesTotal: toStringArray(payload?.stagesTotal),
    progressCompleted: toNonNegativeInt(payload?.progressCompleted),
    progressTotal: toNonNegativeInt(payload?.progressTotal),
    error: typeof payload?.error === 'string' ? payload.error : undefined,
    failedStep: typeof payload?.failedStep === 'string' ? payload.failedStep : undefined,
  };
}

/**
 * Defensive coercion for stage arrays. Backend should always emit
 * `[]` for empty per partner contract §3.2, but tolerate `null` /
 * missing / wrong-typed values so a single bad payload does not crash
 * the UI.
 *
 * Exported for unit testing; not intended for use outside this module.
 */
export function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/**
 * Coerce an untrusted numeric field to a non-negative integer, defaulting to
 * 0 for missing / NaN / negative values so a bad payload never produces a
 * negative or fractional progress reading.
 *
 * Exported for unit testing; not intended for use outside this module.
 */
export function toNonNegativeInt(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

/**
 * Compute integer-rounded stage progress (0–100) from completed/total counts.
 * Returns 0 when `total <= 0` to avoid division by zero on the early
 * `pending` window. Clamps defensively against backend bugs that send
 * `completed > total`.
 */
export function computeStageProgress(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
}

/**
 * Effective analysis progress percentage (0–100), preferring the partner's
 * fine-grained `progress` counter and falling back to stage cardinality when
 * the partner hasn't published numeric progress yet (`progressTotal === 0`).
 *
 * The numeric counter advances *within* a stage, so it yields a far smoother
 * bar than stage cardinality — e.g. 45/133 reads as 34% while stagesCompleted
 * is still empty (which would otherwise show a stuck 0%).
 */
export function computeAnalysisProgress(payload: ExternalAnalysisStatusPayload): number {
  if (payload.progressTotal > 0) {
    return computeStageProgress(payload.progressCompleted, payload.progressTotal);
  }
  return computeStageProgress(payload.stagesCompleted.length, payload.stagesTotal.length);
}
